import {
  Text,
  Loader,
  Alert,
  Box,
  Stack,
  Paper,
  Title,
  Group,
  Button,
  Table,
  Tabs,
  Accordion,
} from '@mantine/core';
import { IconAlertCircle, IconSettings } from '@tabler/icons-react';
import {
  PlanType,
  DisplayPlanGroup,
  CANONICAL_DISPLAY_ORDER,
  DISPLAY_GROUP_LABELS,
  DISPLAY_GROUP_TO_PLAN_TYPES,
  PLAN_AVAILABILITY_GROUPS,
} from '@/types/planTypes';
import { formatCurrency, formatDateMMMYYYY } from '@/utils/formatters';
import type { Dispatch, SetStateAction } from 'react';
import { useState, useMemo, Fragment } from 'react';
import { AssumptionsPanel } from './assumptions-panel';
import type {
  AllPlanSummaries,
  DetailedPlanSummary,
  PlanSummaryData,
} from '@/types/planSummaryTypes';
import { UserSurveyData } from '@/components/survey/user-survey';

// Define tab values
type SummaryTabView = 'combined' | 'user' | 'partner';

interface PlanSummaryTableProps {
  allPlanSummaries: AllPlanSummaries | null;
  isLoading: boolean;
  error: string | null;
  setError?: Dispatch<SetStateAction<string | null>>;
  showPartnerTabPredicate: boolean;
  surveyData: UserSurveyData | null;
  isMobile?: boolean;
}

export function PlanSummaryTable({
  allPlanSummaries,
  isLoading,
  error,
  setError,
  showPartnerTabPredicate,
  surveyData,
  isMobile,
}: PlanSummaryTableProps) {
  const [isAssumptionsPanelOpen, setIsAssumptionsPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SummaryTabView>('combined');

  const hasPartnerData = useMemo(() => {
    if (!allPlanSummaries) return false;
    // Check if there's any partner-specific data calculated
    return Object.values(allPlanSummaries).some((summary) => summary.partner !== null);
  }, [allPlanSummaries]);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={450} mih={200}>
        <Loader />
        <Text>Calculating all plan summaries...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Paper shadow="none" p="md" radius="md" h={450} mih={200}>
        <Alert
          title="Error"
          color="red"
          icon={<IconAlertCircle size={18} />}
          withCloseButton={!!setError}
          onClose={
            setError
              ? () => {
                  setError(null);
                }
              : undefined
          }
          radius="md"
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!allPlanSummaries || Object.keys(allPlanSummaries).length === 0) {
    return (
      <Paper shadow="none" p="md" radius="md">
        <Text>No plan summary data available.</Text>
      </Paper>
    );
  }

  // Group plans by availability
  const groupedPlanItems = PLAN_AVAILABILITY_GROUPS.map((group) => {
    const plansInGroup = group.plans.filter((plan) => CANONICAL_DISPLAY_ORDER.includes(plan));

    const planItemsInGroup = plansInGroup.map((displayPlanGroup) => {
      const displayGroupLabel = DISPLAY_GROUP_LABELS[displayPlanGroup];

      // Helper to get the specific plan type for an individual based on PSLF preference
      function getIndividualPlanType(
        borrowerIsPslf: boolean,
        group: DisplayPlanGroup
      ): PlanType | null {
        const potentialPlanTypes = DISPLAY_GROUP_TO_PLAN_TYPES[group];
        if (potentialPlanTypes.length === 0) return null;

        let chosenPlanType: PlanType | null = null;
        // Prefer exact match based on PSLF status
        for (const pt of potentialPlanTypes) {
          const isPslfPlanVariant = pt.endsWith('_PSLF');
          if (borrowerIsPslf && isPslfPlanVariant) {
            chosenPlanType = pt;
            break;
          } else if (!borrowerIsPslf && !isPslfPlanVariant) {
            chosenPlanType = pt;
            break;
          }
        }
        // Fallback: if no exact match, try to pick non-PSLF if user is not PSLF, or PSLF if user is PSLF.
        // If still no match, pick the first non-PSLF, then first PSLF, then first overall.
        if (!chosenPlanType) {
          if (!borrowerIsPslf) {
            chosenPlanType = potentialPlanTypes.find((pt) => !pt.endsWith('_PSLF')) || null;
          } else {
            chosenPlanType = potentialPlanTypes.find((pt) => pt.endsWith('_PSLF')) || null;
          }
        }
        if (!chosenPlanType) {
          chosenPlanType =
            potentialPlanTypes.find((pt) => !pt.endsWith('_PSLF')) || potentialPlanTypes[0] || null;
        }
        return chosenPlanType;
      }

      let dataForActiveTab: PlanSummaryData | null | undefined = null;

      if (activeTab === 'user') {
        const userPslf = surveyData?.pslfEligible ?? false;
        const usersPlanType = getIndividualPlanType(userPslf, displayPlanGroup);
        if (usersPlanType) {
          const planSummary = allPlanSummaries[usersPlanType];
          dataForActiveTab = planSummary.user;
        } else {
          // No plan type could be determined for user
          return (
            <Table.Tr key={displayPlanGroup}>
              <Table.Td colSpan={isMobile ? 1 : 6}>
                <Text c="dimmed" fs="italic">
                  Plan type not found for {displayGroupLabel} (User).
                </Text>
              </Table.Td>
            </Table.Tr>
          );
        }
      } else if (activeTab === 'partner' && showPartnerTabPredicate && hasPartnerData) {
        const partnerPslf = surveyData?.pslfEligiblePartner ?? false;
        const partnersPlanType = getIndividualPlanType(partnerPslf, displayPlanGroup);
        if (partnersPlanType) {
          const planSummary = allPlanSummaries[partnersPlanType];

          dataForActiveTab = planSummary.partner;
        } else {
          return (
            <Table.Tr key={displayPlanGroup}>
              <Table.Td colSpan={isMobile ? 1 : 6}>
                <Text c="dimmed" fs="italic">
                  Plan type not found for {displayGroupLabel} (Partner).
                </Text>
              </Table.Td>
            </Table.Tr>
          );
        }
      } else if (activeTab === 'combined') {
        const userPslf = surveyData?.pslfEligible ?? false;
        const usersPlanType = getIndividualPlanType(userPslf, displayPlanGroup);
        const userPlanSummaryForCombined = usersPlanType ? allPlanSummaries[usersPlanType] : null;
        const userData = userPlanSummaryForCombined?.user ?? null;

        let partnerData: PlanSummaryData | null = null;
        let partnersPlanType: PlanType | null = null;
        let partnerPlanSummaryForCombined: DetailedPlanSummary | null | undefined = null;

        if (surveyData?.includePartner && hasPartnerData) {
          const partnerPslf = surveyData.pslfEligiblePartner;
          partnersPlanType = getIndividualPlanType(partnerPslf, displayPlanGroup);
          partnerPlanSummaryForCombined = partnersPlanType
            ? allPlanSummaries[partnersPlanType]
            : null;
          partnerData = partnerPlanSummaryForCombined?.partner ?? null;
        }

        if (userData || partnerData) {
          dataForActiveTab = {
            firstMonthlyPayment:
              (userData?.firstMonthlyPayment ?? 0) + (partnerData?.firstMonthlyPayment ?? 0),
            maxMonthlyPayment:
              (userData?.maxMonthlyPayment ?? userData?.firstMonthlyPayment ?? 0) +
              (partnerData?.maxMonthlyPayment ?? partnerData?.firstMonthlyPayment ?? 0),
            totalPayment: (userData?.totalPayment ?? 0) + (partnerData?.totalPayment ?? 0),
            taxesOwedOnForgivenRaw:
              (userData?.taxesOwedOnForgivenRaw ?? 0) + (partnerData?.taxesOwedOnForgivenRaw ?? 0),
            finishDate: null,
            totalForgiven: (userData?.totalForgiven ?? 0) + (partnerData?.totalForgiven ?? 0),
            totalLoanPaymentsDiscounted:
              (userData?.totalLoanPaymentsDiscounted ?? 0) +
              (partnerData?.totalLoanPaymentsDiscounted ?? 0),
          };

          if (userData?.finishDate && partnerData?.finishDate) {
            dataForActiveTab.finishDate = new Date(
              Math.max(
                new Date(userData.finishDate).getTime(),
                new Date(partnerData.finishDate).getTime()
              )
            );
          } else if (userData?.finishDate) {
            dataForActiveTab.finishDate = userData.finishDate;
          } else if (partnerData?.finishDate) {
            dataForActiveTab.finishDate = partnerData.finishDate;
          }
          // If only one set of data exists, ensure maxMonthlyPayment uses its own firstMonthlyPayment as fallback if max is null.
          // The sum (A.max ?? A.first) + (B.max ?? B.first) handles this correctly.
        } else {
          // Neither userData nor partnerData could be found, and at least one plan type was attempted
          const userMsg =
            'User: ' +
            (usersPlanType
              ? userPlanSummaryForCombined?.user
                ? 'OK'
                : `data missing for ${usersPlanType}`
              : 'no plan type');
          let partnerMsg = '';
          if (surveyData?.includePartner && hasPartnerData) {
            partnerMsg =
              ', Partner: ' +
              (partnersPlanType
                ? partnerPlanSummaryForCombined?.partner
                  ? 'OK'
                  : `data missing for ${partnersPlanType}`
                : 'no plan type');
          } else if (surveyData?.includePartner && !hasPartnerData) {
            partnerMsg = ', Partner: no loan data';
          }

          if (
            (usersPlanType && !userPlanSummaryForCombined?.user) ||
            (surveyData?.includePartner &&
              hasPartnerData &&
              partnersPlanType &&
              !partnerPlanSummaryForCombined?.partner)
          ) {
            return (
              <Table.Tr key={displayPlanGroup}>
                <Table.Td colSpan={isMobile ? 1 : 6}>
                  <Text c="dimmed" fs="italic">
                    Data not available for {displayGroupLabel}. ({userMsg}
                    {partnerMsg})
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          }
          // If no plan types were found at all for either, eligibilityForDisplay remains gray N/A
        }
      } else {
        return (
          <Table.Tr key={displayPlanGroup}>
            <Table.Td colSpan={isMobile ? 1 : 6}>
              <Text c="dimmed" fs="italic">
                Invalid tab selection.
              </Text>
            </Table.Td>
          </Table.Tr>
        );
      }
      // Fallback for rows where dataForActiveTab ended up null/undefined despite logic above
      // This can happen if plan types were found but data was missing, and we didn't return an error TR.
      if (!dataForActiveTab && (activeTab === 'user' || activeTab === 'partner')) {
        return (
          <Table.Tr key={displayPlanGroup}>
            <Table.Td colSpan={isMobile ? 1 : 6}>
              <Text c="dimmed" fs="italic">
                Calculated data not available for {displayGroupLabel} on {activeTab} tab.
              </Text>
            </Table.Td>
          </Table.Tr>
        );
      }
      // For combined tab, if dataForActiveTab is null, it means both userData and partnerData were null.
      // The gray N/A cells will render due to displaySummary having nulls.
      // The eligibilityForDisplay would have been set to gray 'N/A' if both were null and no plan types found,
      // or specific error message returned if plan types found but data missing.

      const displaySummary = {
        firstMonthlyPayment: dataForActiveTab?.firstMonthlyPayment,
        maxMonthlyPayment: dataForActiveTab?.maxMonthlyPayment,
        finishDate: dataForActiveTab?.finishDate,
        totalPayment: dataForActiveTab?.totalPayment,
        taxesOwedOnForgivenRaw: dataForActiveTab?.taxesOwedOnForgivenRaw,
        totalForgiven: dataForActiveTab?.totalForgiven,
      };

      if (isMobile) {
        // Mobile view: Render Accordion.Item
        return (
          <Accordion.Item key={displayPlanGroup} value={displayPlanGroup}>
            <Accordion.Control>
              <Group justify="space-between" wrap="nowrap" align="flex-start" mr="md">
                <Text fw={500}>{displayGroupLabel}</Text>
                {displaySummary.firstMonthlyPayment !== null &&
                  displaySummary.firstMonthlyPayment !== undefined && (
                    <Text size="sm" miw="85px" ta="right" pt="4px">
                      {formatCurrency(displaySummary.firstMonthlyPayment)} / mo
                    </Text>
                  )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Max monthly payment:</Text>
                  <Text size="sm" fw={500}>
                    {formatCurrency(displaySummary.maxMonthlyPayment)}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Payoff date:</Text>
                  <Text size="sm" fw={500}>
                    {displaySummary.finishDate ? formatDateMMMYYYY(displaySummary.finishDate) : '-'}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Total payments (est.):</Text>
                  <Text size="sm" fw={500}>
                    {formatCurrency(displaySummary.totalPayment, '-', false, true)}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Amount cancelled (est.):</Text>
                  <Text size="sm" fw={500} ta="right">
                    {displaySummary.totalForgiven
                      ? formatCurrency(displaySummary.totalForgiven, '-', false, true)
                      : '-'}
                  </Text>
                </Group>
                {displaySummary.taxesOwedOnForgivenRaw != null &&
                  displaySummary.taxesOwedOnForgivenRaw > 0 && (
                    <Group justify="space-between">
                      <Text size="sm">Taxes owed: </Text>
                      <Text size="sm" fw={500} ta="right">
                        {formatCurrency(displaySummary.taxesOwedOnForgivenRaw, '-', false, true)}
                      </Text>
                    </Group>
                  )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        );
      }
      // Desktop view: Render Table.Tr
      return (
        <Table.Tr key={displayPlanGroup}>
          <Table.Td>
            <Text fw={500}>{displayGroupLabel}</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" ta="right">
              {formatCurrency(displaySummary.firstMonthlyPayment)}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" ta="right">
              {formatCurrency(displaySummary.maxMonthlyPayment)}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" ta="right">
              {displaySummary.finishDate ? formatDateMMMYYYY(displaySummary.finishDate) : '-'}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" ta="right">
              {formatCurrency(displaySummary.totalPayment, '-', false, true)}
            </Text>
          </Table.Td>
          <Table.Td>
            <Stack gap={0} align="flex-end">
              <Text size="sm">
                {displaySummary.totalForgiven
                  ? formatCurrency(displaySummary.totalForgiven, '-', false, true)
                  : '-'}
              </Text>
              {displaySummary.taxesOwedOnForgivenRaw != null &&
                displaySummary.taxesOwedOnForgivenRaw > 0 && (
                  <Text size="sm" c="dimmed" ta="right">
                    Taxes owed:{' '}
                    {formatCurrency(displaySummary.taxesOwedOnForgivenRaw, '-', false, true)}
                  </Text>
                )}
            </Stack>
          </Table.Td>
        </Table.Tr>
      );
    });

    return {
      group,
      planItems: planItemsInGroup,
    };
  });

  return (
    <Box p="md" h="100%" w="100%">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Title order={3}>Repayment plan comparison</Title>
          <Button
            variant="light"
            leftSection={<IconSettings size={16} />}
            onClick={() => {
              setIsAssumptionsPanelOpen((prev) => !prev);
            }}
          >
            {isAssumptionsPanelOpen ? 'Hide assumptions' : 'Adjust assumptions'}
          </Button>
        </Group>

        {isAssumptionsPanelOpen && (
          <AssumptionsPanel
            onApply={() => {
              setIsAssumptionsPanelOpen(false);
            }}
            onCancel={() => {
              setIsAssumptionsPanelOpen(false);
            }}
          />
        )}

        <Tabs
          value={activeTab}
          onChange={(value) => {
            setActiveTab(value as SummaryTabView);
          }}
        >
          {showPartnerTabPredicate && hasPartnerData && (
            <Tabs.List grow>
              <Tabs.Tab value="combined">Total</Tabs.Tab>
              <Tabs.Tab value="user">My loans</Tabs.Tab>
              <Tabs.Tab value="partner">Partner's loans</Tabs.Tab>
            </Tabs.List>
          )}

          <Tabs.Panel value={activeTab} pt="md">
            {isMobile ? (
              <Stack gap="lg">
                {groupedPlanItems.map(({ group, planItems }) => (
                  <Box key={group.id}>
                    <Box mb="md">
                      <Text fw={600} size="md" mb="xs">
                        {group.label}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {group.description}
                      </Text>
                    </Box>
                    <Accordion variant="separated" radius="md">
                      {planItems}
                    </Accordion>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Table
                verticalSpacing="xs"
                withTableBorder
                layout="fixed"
                styles={{
                  td: { verticalAlign: 'top' },
                  th: { verticalAlign: 'top' },
                  tbody: { verticalAlign: 'top' },
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th rowSpan={2} style={{ textAlign: 'left' }} w="25%">
                      Plan
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'center' }} colSpan={2} w="20%">
                      Monthly payments
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }} rowSpan={2} w="15">
                      Finish date
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }} rowSpan={2} w="20%">
                      Total payments (est.)
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }} rowSpan={2} w="20%">
                      Amount cancelled (est.)
                    </Table.Th>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Th
                      style={{
                        fontWeight: 500,
                        paddingTop: 4,
                        paddingBottom: 4,
                        textAlign: 'right',
                      }}
                    >
                      First
                    </Table.Th>
                    <Table.Th
                      style={{
                        fontWeight: 500,
                        paddingTop: 4,
                        paddingBottom: 4,
                        textAlign: 'right',
                      }}
                    >
                      Max
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groupedPlanItems.map(({ group, planItems }) => (
                    <Fragment key={group.id}>
                      <Table.Tr>
                        <Table.Td
                          colSpan={6}
                          style={{
                            backgroundColor: 'var(--mantine-color-gray-1)',
                            borderTop: '2px solid var(--mantine-color-gray-4)',
                            paddingTop: 12,
                            paddingBottom: 12,
                          }}
                        >
                          <Stack gap="xs">
                            <Text fw={600} size="sm">
                              {group.label}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {group.description}
                            </Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                      {planItems}
                    </Fragment>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Box>
  );
}
